import { QdzElement, define } from '@qdz/element';

const CM_PER_IN = 2.54;
const KG_PER_LB = 0.45359237;

const convert = (text, from, to) => {
  if (from === to) return String(text);
  const factor = to === 'cm' ? CM_PER_IN : 1 / CM_PER_IN;
  return String(text).replace(/\d+(?:\.\d+)?/g, (number) => String(Number((parseFloat(number) * factor).toFixed(1))));
};

export class QdzSizeChart extends QdzElement {
  mount() {
    for (const button of this.refs('unit')) {
      this.listen(button, 'click', () => this.setUnits(button.dataset.unit));
    }
    this.setUnits(this.getAttribute('units') || 'in');
  }

  setUnits(units) {
    for (const button of this.refs('unit')) button.setAttribute('aria-pressed', String(button.dataset.unit === units));
    const base = this.getAttribute('base') === 'cm' ? 'cm' : 'in';
    for (const cell of this.refs('measure')) cell.textContent = convert(cell.dataset.value, base, units);
    for (const suffix of this.refs('suffix')) suffix.textContent = `(${units})`;
  }
}

export class QdzFitFinder extends QdzElement {
  #metric = false;
  #fit = 1;
  #size = '';

  get rules() {
    try {
      let rules = JSON.parse(this.ref('rules')?.textContent || '[]');
      if (typeof rules === 'string') rules = JSON.parse(rules);
      if (Array.isArray(rules)) return rules;
      if (Array.isArray(rules?.rules)) return rules.rules;
      return Object.entries(rules ?? {}).map(([size, rule]) => ({ size, ...rule }));
    } catch {
      return [];
    }
  }

  mount() {
    this.#metric = this.getAttribute('units') === 'metric';
    if (this.#metric) this.#convert(true);
    for (const button of this.refs('unit')) {
      this.listen(button, 'click', () => this.#convert(button.dataset.unit === 'metric'));
    }
    this.refs('fit').forEach((button, index) => {
      this.listen(button, 'click', () => {
        this.#fit = index;
        this.update();
      });
    });
    for (const name of ['height', 'weight', 'chest']) if (this.ref(name)) this.listen(this.ref(name), 'input', () => this.update());
    this.listen(this.ref('select'), 'click', () => this.#select());
    this.update();
  }

  #convert(metric) {
    const height = this.ref('height');
    const weight = this.ref('weight');
    const chest = this.ref('chest');
    if (metric !== this.#metric) {
      const h = Number(height.value);
      const w = Number(weight.value);
      this.#metric = metric;
      this.#range(height, metric ? [140, 210] : [55, 83], metric ? Math.round(h * CM_PER_IN) : Math.round(h / CM_PER_IN));
      this.#range(weight, metric ? [40, 160] : [90, 350], metric ? Math.round(w * KG_PER_LB) : Math.round(w / KG_PER_LB));
      if (chest) this.#range(chest, metric ? [71, 152] : [28, 60], metric ? Math.round(Number(chest.value) * CM_PER_IN) : Math.round(Number(chest.value) / CM_PER_IN));
    } else if (metric) {
      this.#range(height, [140, 210], Math.round(Number(height.value) * CM_PER_IN));
      this.#range(weight, [40, 160], Math.round(Number(weight.value) * KG_PER_LB));
      if (chest) this.#range(chest, [71, 152], Math.round(Number(chest.value) * CM_PER_IN));
    }
    this.ref('height-unit').textContent = metric ? 'cm' : 'in';
    this.ref('weight-unit').textContent = metric ? 'kg' : 'lb';
    if (chest) this.ref('chest-unit').textContent = metric ? 'cm' : 'in';
    for (const button of this.refs('unit')) button.setAttribute('aria-pressed', String((button.dataset.unit === 'metric') === metric));
    this.update();
  }

  #range(input, [min, max], value) {
    input.min = min;
    input.max = max;
    input.value = Math.min(Math.max(value, min), max);
  }

  update() {
    const height = Number(this.ref('height').value);
    const weight = Number(this.ref('weight').value);
    const inches = this.#metric ? height / CM_PER_IN : height;
    const pounds = this.#metric ? weight / KG_PER_LB : weight;
    const chest = this.ref('chest');
    const chestInches = chest ? (this.#metric ? Number(chest.value) / CM_PER_IN : Number(chest.value)) : 0;
    const rules = this.rules;
    const fitsChest = (rule) => !chest || rule.chest === undefined || rule.chest === null || chestInches <= Number(rule.chest);
    let index = rules.findIndex((rule) => inches <= Number(rule.height) && pounds <= Number(rule.weight) && fitsChest(rule));
    if (index < 0) index = rules.length - 1;
    index = Math.max(0, Math.min(rules.length - 1, index + this.#fit - 1));
    this.#size = String(rules[index]?.size ?? '');

    const feet = Math.floor(Math.round(inches) / 12);
    const heightText = this.#metric ? `${height} cm` : `${feet}′${Math.round(inches) - feet * 12}″`;
    const weightText = this.#metric ? `${weight} kg` : `${weight} lb`;
    this.ref('height-value').textContent = heightText;
    this.ref('weight-value').textContent = weightText;
    if (chest) this.ref('chest-value').textContent = `${chest.value} ${this.#metric ? 'cm' : 'in'}`;
    this.refs('fit').forEach((button, position) => button.setAttribute('aria-pressed', String(position === this.#fit)));
    const fit = this.refs('fit')[this.#fit]?.textContent.trim().toLowerCase() ?? '';
    this.ref('size').textContent = this.#size;
    this.ref('suggest').textContent = this.dataset.suggest.replace('[size]', this.#size);
    const note = this.getAttribute('note');
    const reason = this.dataset.reason.replace('[height]', heightText).replace('[weight]', weightText).replace('[fit]', fit);
    this.ref('reason').textContent = note ? `${note} ${reason}` : reason;
    this.ref('select').textContent = this.dataset.select.replace('[size]', this.#size);
  }

  #select() {
    const dialog = document.getElementById(this.getAttribute('dialog-id'));
    const trigger = document.querySelector(`[data-qdz-dialog-open="${this.getAttribute('dialog-id')}"]`);
    const scope = trigger?.closest('fieldset') ?? trigger?.closest('[data-qdz-product]') ?? document;
    const target = this.#size.toLowerCase();
    dialog?.close?.();
    const event = new CustomEvent('qdz:fit:select', { bubbles: true, cancelable: true, detail: { size: this.#size } });
    if (!(trigger ?? this).dispatchEvent(event)) return;
    const input = [...scope.querySelectorAll('input[type="radio"]')].find((element) => element.value.toLowerCase() === target);
    if (input && !input.checked) input.click();
  }
}

export class QdzSizeGuidePage extends QdzElement {
  mount() {
    const select = this.ref('switch');
    if (!select) return;
    this.ref('switcher').hidden = false;
    this.listen(select, 'change', () => this.show(select.value, true));
    this.listen(window, 'hashchange', () => this.#fromHash());
    this.#fromHash();
  }

  #fromHash() {
    const handle = decodeURIComponent(window.location.hash.slice(1));
    this.show(this.refs('profile').some((profile) => profile.id === handle) ? handle : '', false);
  }

  show(handle, updateUrl) {
    for (const profile of this.refs('profile')) profile.hidden = handle !== '' && profile.id !== handle;
    for (const group of this.refs('group')) group.hidden = !group.querySelector('[data-ref="profile"]:not([hidden])');
    this.ref('switch').value = handle;
    if (updateUrl) window.history.replaceState(window.history.state, '', handle ? `#${handle}` : window.location.pathname + window.location.search);
  }
}

define('qdz-size-chart', QdzSizeChart);
define('qdz-fit-finder', QdzFitFinder);
define('qdz-size-guide-page', QdzSizeGuidePage);
